import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useBillingSettings } from '@/hooks/useBillingSettings';
import { usePatients } from '../patients/usePatients';
import { useBills } from './useBills';
import { useCreateOpdBill } from './useCreateOpdBill';
import { openBillPdf } from './api';
import { PrintBillButton } from '@/features/printing/PrintBillButton';
import RecordPaymentDialog from './RecordPaymentDialog';
import IssueCreditNoteDialog from './IssueCreditNoteDialog';
import CreditNotesPanel from './CreditNotesPanel';
import { keys } from '@/lib/queryKeys';
import { getSpecialties } from '../admin/api';
import { OpdFeeLinesEditor, billTotals, lineProblems, toBillItems, useOpdBillLines } from '../opd-fees/OpdFeeLines';
import type { Bill, PaymentMode, PaymentStatus } from './types';

/**
 * Fourth screen migrated off Create React App — see HMS_V4_API_CONTRACT.md
 * and QueueView.tsx for the established pattern: a drop-in replacement for
 * src/components/BillingModule.jsx's named `BillingModule` export, swapped
 * into App.jsx by changing one import line. `ReportsModule`, which shared
 * that file, was extracted into its own src/components/ReportsModule.jsx
 * first (see that file's header) — it is a separate migration item later in
 * the doc's order and this pass does not touch it.
 *
 * Scope, matching the legacy screen exactly: reception-facing OPD invoice
 * generation (registration/consultation/procedure fee lines) plus the bill
 * list with PDF/mark-paid. Pharmacy and IP billing happen through their own
 * dedicated flows elsewhere (routes/billing.js's pharmacy/create, and
 * routes/ip.js's discharge bundling) and are out of scope here, same as in
 * the legacy screen.
 *
 * One deliberate deviation from strict parity: a successful bill generation
 * shows an inline success Card instead of a native alert(). Every other
 * screen migrated this session (Queue, Appointments) already uses this exact
 * inline-banner pattern for "action just succeeded" — alert() was the odd
 * one out, not a considered design choice to preserve. Consistency across
 * the new design system is the point of this migration (see the doc's own
 * "Consistent User Experience" goal), so this one native-dialog replacement
 * is a correction toward the pattern already established, not a redesign.
 */

interface BillingModuleProps {
  preselectBillId?: number | null;
  onPreselectConsumed?: () => void;
}

interface BillFormState {
  uhid: string;
  payMode: PaymentMode;
  payStatus: PaymentStatus;
}

const INITIAL_FORM: BillFormState = {
  uhid: '',
  payMode: 'Cash',
  payStatus: 'Paid',
};

export default function BillingModule({ preselectBillId, onPreselectConsumed }: BillingModuleProps) {
  const [form, setForm] = useState<BillFormState>(INITIAL_FORM);
  // No visit here, so the department and first-visit/returning choice are
  // the biller's: they decide which fees the OPD fee schedule applies and
  // whether Registration is added (features/opd-fees/OpdFeeLines.tsx).
  const [specialtyId, setSpecialtyId] = useState<number | null>(null);
  const [newPatient, setNewPatient] = useState(true);
  const [patientQuery, setPatientQuery] = useState('');
  const [confirmedBill, setConfirmedBill] = useState<Bill | null>(null);
  const [paymentDialogBill, setPaymentDialogBill] = useState<Bill | null>(null);
  const [creditNoteDialogBill, setCreditNoteDialogBill] = useState<Bill | null>(null);
  const { gstEnabled, gstPercentageOptions } = useBillingSettings();
  const { quoteQuery, quote, lines, setLines, reset: resetLines } = useOpdBillLines({ specialtyId, newPatient });
  const specialtiesQuery = useQuery({ queryKey: keys.config.specialties(), queryFn: () => getSpecialties(), staleTime: 300_000 });

  const patientsQuery = usePatients();
  const billsQuery = useBills();
  const createBill = useCreateOpdBill();
  // No dedicated hook file, unlike the other mutations here — this one never
  // touches the query cache (it opens a blob in a new tab), so a shared
  // useMutation wrapper would exist only to hold a mutationFn, and .isPending/
  // .variables (used below to show which row is loading) come for free either way.
  const viewPdf = useMutation({ mutationFn: openBillPdf });

  const patients = patientsQuery.data ?? [];
  const bills = billsQuery.data ?? [];
  const sel = patients.find((p) => p.uhid === form.uhid);

  const patientMatches = useMemo(() => {
    const q = patientQuery.trim().toLowerCase();
    if (!q) return [];
    return patients
      .filter((p) => p.name?.toLowerCase().includes(q) || p.uhid?.toLowerCase().includes(q) || p.mobile?.includes(patientQuery))
      .slice(0, 8);
  }, [patients, patientQuery]);

  const lineItems = useMemo(() => toBillItems(lines, quote, gstEnabled), [lines, quote, gstEnabled]);
  const problems = lineProblems(lines, quote);
  // Mirrors utils/billingPricing.js's gross->discount->net->GST order so the
  // preview here matches exactly what the backend will compute and store.
  const { subtotal: sub, discount: discountAmt, gst: gstAmt, total } = billTotals(lineItems);

  const f = <K extends keyof BillFormState>(k: K, v: BillFormState[K]) => setForm((p) => ({ ...p, [k]: v }));

  // Arriving here from the Dashboard's global search ("open this bill") —
  // wait for the bill list to finish loading, then open the same PDF viewer
  // the row's own button uses, automatically instead of requiring the staff
  // member to find it in the list themselves. Guarded so it fires at most once.
  const preselectHandledRef = useRef(false);
  useEffect(() => {
    if (!preselectBillId || billsQuery.isLoading || preselectHandledRef.current) return;
    preselectHandledRef.current = true;
    viewPdf.mutate(preselectBillId);
    onPreselectConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectBillId, billsQuery.isLoading]);

  const generate = () => {
    if (!sel || !lineItems.length || problems.length) return;
    setConfirmedBill(null);
    createBill.mutate(
      { patient_id: sel.id, items: lineItems, payment_mode: form.payMode, payment_status: form.payStatus, ...(specialtyId ? { specialty_id: specialtyId } : {}) },
      {
        onSuccess: (bill) => {
          setConfirmedBill(bill);
          setForm(INITIAL_FORM);
          setPatientQuery('');
          resetLines();
        },
      }
    );
  };

  const createError = createBill.error instanceof ApiError ? createBill.error.message : null;

  return (
    <div className="fade-in grid gap-4 min-[900px]:grid-cols-2">
      <Card>
        <div className="mb-4 text-sm font-semibold">New OPD bill</div>

        {createError && <div className="mb-3.5 rounded-(--radius) bg-danger-soft px-3 py-2 text-xs text-danger-ink">{createError}</div>}

        {confirmedBill && (
          <div className="mb-3.5 rounded-(--radius) border border-success-soft bg-success-soft px-3.5 py-2.5 text-[13px] text-success-ink">
            ✓ Bill {confirmedBill.bill_number} for ₹{parseFloat(confirmedBill.total_amount).toFixed(2)} generated
            {confirmedBill.payment_status === 'Pending' ? ' — marked Pending, settle it from the bill list when paid.' : '.'}
          </div>
        )}

        <div className="mb-3.5">
          <FormField label="Patient" required>
            {sel ? (
              <div className="flex items-center justify-between rounded-(--radius) border border-success-soft bg-success-soft px-3 py-2.5">
                <div>
                  <div className="text-[13px] font-bold text-success-ink">✓ {sel.name}</div>
                  <div className="text-[11px] text-success-ink">
                    {sel.uhid} · {sel.gender}, {sel.age}y{sel.mobile ? ` · ${sel.mobile}` : ''}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { f('uhid', ''); setPatientQuery(''); }}>
                  Change
                </Button>
              </div>
            ) : (
              <div>
                <Input
                  placeholder="Search name, UHID, mobile…"
                  value={patientQuery}
                  onChange={(e) => setPatientQuery(e.target.value)}
                />
                {patientQuery.trim() && (
                  <div className="mt-1.5 max-h-55 overflow-y-auto rounded-(--radius) border border-line">
                    {patientMatches.length === 0 ? (
                      <div className="p-3 text-center text-xs text-ink-muted">No patients found for &quot;{patientQuery}&quot;</div>
                    ) : (
                      patientMatches.map((p) => (
                        <div
                          key={p.id}
                          onClick={() => { f('uhid', p.uhid); setPatientQuery(''); }}
                          className="flex cursor-pointer items-center justify-between border-b border-[#F3F4F6] px-3 py-2.5 last:border-b-0 hover:bg-canvas"
                        >
                          <div>
                            <div className="text-[13px] font-semibold">{p.name}</div>
                            <div className="text-[11px] text-ink-muted">
                              {p.uhid} · {p.gender}, {p.age}y{p.mobile ? ` · ${p.mobile}` : ''}
                            </div>
                          </div>
                          <span className="text-base text-teal">→</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </FormField>
        </div>

        <div className="mb-3 grid gap-2 min-[520px]:grid-cols-2">
          <FormField label="Department">
            <NativeSelect value={specialtyId ?? ''} onChange={(e) => setSpecialtyId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Any — default fees</option>
              {(specialtiesQuery.data ?? []).filter((s) => s.active).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField label="Patient">
            <NativeSelect value={newPatient ? 'new' : 'returning'} onChange={(e) => setNewPatient(e.target.value === 'new')}>
              <option value="new">First visit (adds registration)</option>
              <option value="returning">Returning patient</option>
            </NativeSelect>
          </FormField>
        </div>

        {quoteQuery.isLoading ? (
          <LoadingState label="Loading the fee schedule…" />
        ) : quoteQuery.isError || !quote ? (
          <div className="mb-3 rounded-(--radius) bg-danger-soft px-3 py-2 text-xs text-danger-ink">
            Could not load the OPD fee schedule.{' '}
            <button type="button" className="underline" onClick={() => quoteQuery.refetch()}>Try again</button>
          </div>
        ) : (
          <OpdFeeLinesEditor quote={quote} lines={lines} onChange={setLines} gstEnabled={gstEnabled} gstOptions={gstPercentageOptions} />
        )}
        {problems.length > 0 && (
          <div className="mt-2 rounded-(--radius) bg-warn-soft px-3 py-2 text-xs text-warn-ink">{problems.join(' · ')}</div>
        )}

        <div className="mt-3.5" />
        <div className="mb-3.5 rounded-(--radius) bg-canvas p-3">
          {(
            [
              ['Subtotal', sub],
              ...(discountAmt > 0 ? [['Discount', -discountAmt] as [string, number]] : []),
              ...(gstEnabled ? [['GST', gstAmt] as [string, number]] : []),
              ['Total', total],
            ] as Array<[string, number]>
          ).map(
            ([l, v], i, arr) => (
              <div
                key={l}
                className={`flex justify-between ${i < arr.length - 1 ? 'mb-1.5' : ''} ${i === arr.length - 1 ? 'border-t border-line pt-2 text-base font-bold' : 'text-[13px]'}`}
              >
                {l}
                <span className={v < 0 ? 'text-danger-ink' : undefined}>
                  {v < 0 ? `− ₹${Math.abs(v).toFixed(2)}` : `₹${v.toFixed(2)}`}
                </span>
              </div>
            )
          )}
        </div>

        <div className="grid2 mb-3.5">
          <FormField label="Payment mode">
            <NativeSelect value={form.payMode} onChange={(e) => f('payMode', e.target.value as PaymentMode)}>
              {(['Cash', 'UPI', 'Card'] as const).map((m) => (
                <option key={m}>{m}</option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField label="Payment status">
            <NativeSelect value={form.payStatus} onChange={(e) => f('payStatus', e.target.value as PaymentStatus)}>
              <option value="Paid">Paid now</option>
              <option value="Pending">Pending — bill now, collect later</option>
            </NativeSelect>
          </FormField>
        </div>

        <Button full disabled={!sel || !lineItems.length || problems.length > 0 || createBill.isPending} loading={createBill.isPending} onClick={generate}>
          {createBill.isPending ? 'Generating…' : gstEnabled ? 'Generate GST invoice' : 'Generate invoice'}
        </Button>
      </Card>

      <Card>
        <div className="mb-3.5 text-sm font-semibold">Recent bills</div>
        {billsQuery.isLoading ? (
          <LoadingState />
        ) : bills.length === 0 ? (
          <EmptyState label="No bills generated yet" />
        ) : (
          <div className="flex flex-col gap-2.5">
            {bills.map((b) => (
              <div key={b.id} className="rounded-(--radius) border border-line p-2.75">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-mono text-[13px] font-bold text-teal">{b.bill_number}</div>
                    <div className="mt-0.5 text-xs text-ink-muted">
                      {b.name} · {new Date(b.created_at).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[15px] font-bold">₹{parseFloat(b.total_amount).toFixed(2)}</div>
                    <Badge variant={b.bill_type === 'OPD' ? 'info' : b.bill_type === 'IP' ? 'warn' : 'success'}>{b.bill_type}</Badge>
                  </div>
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-ink-muted">{b.payment_mode}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={viewPdf.isPending && viewPdf.variables === b.id}
                      onClick={() => viewPdf.mutate(b.id)}
                    >
                      {viewPdf.isPending && viewPdf.variables === b.id ? '…' : '📄 PDF'}
                    </Button>
                    <PrintBillButton billId={b.id} />
                    {b.bill_type !== 'Pharmacy' && b.sale_status !== 'cancelled' && (
                      <Button variant="ghost" size="sm" onClick={() => setCreditNoteDialogBill(b)}>
                        Credit note
                      </Button>
                    )}
                  </div>
                  {b.payment_status !== 'Paid' ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="warn">{b.payment_status}</Badge>
                      <Button variant="ghost" size="sm" onClick={() => setPaymentDialogBill(b)}>
                        Record payment
                      </Button>
                    </div>
                  ) : (
                    <Badge variant="success">Paid</Badge>
                  )}
                </div>
                {b.sale_status === 'partially_returned' && (
                  <div className="mt-1.5">
                    <Badge variant="warn">Partially credited</Badge>
                  </div>
                )}
                {b.sale_status === 'cancelled' && (
                  <div className="mt-1.5">
                    <Badge variant="danger">Fully credited</Badge>
                  </div>
                )}
                <CreditNotesPanel billId={b.id} />
              </div>
            ))}
          </div>
        )}
      </Card>

      {paymentDialogBill && <RecordPaymentDialog bill={paymentDialogBill} onClose={() => setPaymentDialogBill(null)} />}
      {creditNoteDialogBill && (
        <IssueCreditNoteDialog bill={creditNoteDialogBill} onClose={() => setCreditNoteDialogBill(null)} />
      )}
    </div>
  );
}
